import { LinkifySegment, TextSegmentStyle, linkifyText } from './linkify-text.helper';

/**
 * Lightweight markdown for chat bubbles (no library, no innerHTML): **bold**, __bold__,
 * *italic*, _italic_, ***both***, ~~strike~~, `code`, # headings (shown bold),
 * - / * / + bullets (shown as •), > quotes and --- rules. Links still go through linkifyText.
 *
 * Messages are typed out character by character, so a half-typed marker ("**Boo")
 * must not flash: unmatched markers are hidden until the closing one arrives.
 */
export function renderChatMarkdown(text: string): LinkifySegment[] {
  if (!text) {
    return [];
  }

  return parseInline(prepareBlocks(text)).flatMap(run =>
    linkifyText(run.value).map(segment =>
      segment.type === 'text' ? { ...segment, ...run.style } : segment,
    ),
  );
}

interface StyledRun {
  value: string;
  style: TextSegmentStyle;
}

/** Line-level syntax → plain lines (headings become **bold** for the inline pass). */
function prepareBlocks(text: string): string {
  return text
    .split('\n')
    .filter(line => !/^\s*([-*_])(\s*\1){2,}\s*$/.test(line)) // --- / *** / ___
    .map(line =>
      line
        .replace(/^(\s*)#{1,6}\s*$/, '$1') // heading marker typed, text not yet
        .replace(/^(\s*)#{1,6}\s+(.+?)\s*#*\s*$/, '$1**$2**')
        .replace(/^(\s*)>\s?/, '$1')
        .replace(/^(\s*)[-*+]\s+/, '$1• '),
    )
    .join('\n');
}

/**
 * Complete inline markers, in priority order. Single * / _ only wrap words, so
 * trip_id and 2*3 stay as they are.
 */
const INLINE_PATTERN = new RegExp(
  [
    String.raw`(\*\*\*|___)(?=\S)([^\n]+?)(?<=\S)\1`, // 1,2: bold + italic
    String.raw`(\*\*|__)(?=\S)([^\n]+?)(?<=\S)\3`, // 3,4: bold
    String.raw`~~(?=\S)([^\n]+?)(?<=\S)~~`, // 5: strike
    String.raw`\x60([^\x60\n]+)\x60`, // 6: code
    String.raw`(^|[\s(])\*(?=\S)([^*\n]+?)(?<=\S)\*(?=[\s).,!?:;]|$)`, // 7,8: italic
    String.raw`(^|[\s(])_(?=\S)([^_\n]+?)(?<=\S)_(?=[\s).,!?:;]|$)`, // 9,10: italic
    String.raw`\[([^\]\n]+)\](?:\(\s*\))?`, // 11: [text] left after URL removal
  ].join('|'),
  'gm',
);

function parseInline(text: string): StyledRun[] {
  const runs: StyledRun[] = [];
  const pushPlain = (value: string): void => {
    const cleaned = hideStrayMarkers(value);
    if (cleaned) {
      runs.push({ value: cleaned, style: {} });
    }
  };
  const pushStyled = (value: string, style: TextSegmentStyle): void => {
    const cleaned = hideStrayMarkers(value);
    if (cleaned) {
      runs.push({ value: cleaned, style });
    }
  };

  let lastIndex = 0;
  for (const match of text.matchAll(INLINE_PATTERN)) {
    const start = match.index ?? 0;
    pushPlain(text.slice(lastIndex, start));

    if (match[2] !== undefined) {
      pushStyled(match[2], { bold: true, italic: true });
    } else if (match[4] !== undefined) {
      pushStyled(match[4], { bold: true });
    } else if (match[5] !== undefined) {
      pushStyled(match[5], { strike: true });
    } else if (match[6] !== undefined) {
      runs.push({ value: match[6], style: { code: true } });
    } else if (match[8] !== undefined) {
      pushPlain(match[7]);
      pushStyled(match[8], { italic: true });
    } else if (match[10] !== undefined) {
      pushPlain(match[9]);
      pushStyled(match[10], { italic: true });
    } else if (match[11] !== undefined) {
      pushPlain(match[11]);
    }

    lastIndex = start + match[0].length;
  }
  pushPlain(text.slice(lastIndex));

  return runs;
}

/**
 * Markers without a partner (half-typed, or nested inside another style) are hidden.
 * A * between digits (2*3) is kept; single _ is left alone (identifiers).
 */
function hideStrayMarkers(value: string): string {
  return value
    .replace(/\*{2,}|_{2,}|~~|\x60/g, '')
    .replace(/(?<!\d)\*|\*(?!\d)/g, '');
}
